import { useCallback, useEffect, useMemo, useRef } from 'react'
import { StyleProp, ViewStyle } from 'react-native'

import {
  BottomSheetBackdrop,
  BottomSheetBackdropProps,
  BottomSheetHandleProps,
  BottomSheetModal,
  useBottomSheetDynamicSnapPoints,
} from '@gorhom/bottom-sheet'
import { colors } from '@zet/design-system'
import { Stack } from '@zet/design-system/layout'
import { useReducedMotion } from 'react-native-reanimated'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { useLockBodyScroll } from '../../hooks'

export type BottomSheetProps = {
  children?: React.ReactElement
  handleComponent?: React.FC<BottomSheetHandleProps>
  visible?: boolean
  onDismiss?: () => void
  snapPoints?: string[] | number[]
  bodyStyle?: StyleProp<ViewStyle>
  disableBottomSheet: () => void
  sheetName?: string
  feature?: string
  backgroundColor?: string
  enableHandlePanningGesture?: boolean
  enableContentPanningGesture?: boolean
  pressBehavior?: boolean
  disableBackdrop?: boolean
}

export const BottomSheet = (props: BottomSheetProps) => {
  const {
    children,
    visible = null,
    handleComponent,
    onDismiss,
    snapPoints,
    bodyStyle,
    disableBottomSheet,
    sheetName,
    feature,
    backgroundColor,
    enableHandlePanningGesture,
    enableContentPanningGesture,
    pressBehavior,
    disableBackdrop = false,
  } = props

  useLockBodyScroll(visible || false)
  const bottomSheetModalRef = useRef<BottomSheetModal>(null)
  const { bottom: safeAreaBottom } = useSafeAreaInsets()

  useEffect(() => {
    if (visible) {
      bottomSheetModalRef?.current?.present()
    } else if (visible !== null && visible === false) {
      bottomSheetModalRef?.current?.dismiss()
    }
  }, [sheetName, visible, feature])

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        pressBehavior={disableBackdrop ? 'none' : 'close'}
        {...props}
        disappearsOnIndex={-1}
        appearsOnIndex={0}
      />
    ),
    []
  )

  const handleOnDismiss = () => {
    bottomSheetModalRef?.current?.dismiss()
    onDismiss && onDismiss()
  }

  const initialSnapPoints = useMemo(() => {
    if (snapPoints) return [snapPoints, 'CONTENT_HEIGHT']
    else return ['CONTENT_HEIGHT']
  }, [])

  const {
    animatedHandleHeight,
    animatedSnapPoints,
    animatedContentHeight,
    handleContentLayout,
  } = useBottomSheetDynamicSnapPoints(initialSnapPoints)

  const reduceMotion = useReducedMotion()

  return (
    <BottomSheetModal
      animateOnMount={!reduceMotion}
      snapPoints={animatedSnapPoints}
      handleHeight={animatedHandleHeight}
      contentHeight={animatedContentHeight}
      backdropComponent={renderBackdrop}
      handleComponent={handleComponent}
      enableContentPanningGesture={enableContentPanningGesture}
      enableHandlePanningGesture={enableHandlePanningGesture}
      onDismiss={handleOnDismiss}
      ref={bottomSheetModalRef}
      index={0}
      handleIndicatorStyle={{
        backgroundColor: colors.grays[40],
        width: 48,
        marginBottom: -8,
      }}
      backgroundStyle={{
        backgroundColor: backgroundColor || colors.neutral.white100,
        borderTopLeftRadius: 6,
        borderTopRightRadius: 6,
      }}
      onChange={(index) => {
        if (index === -1) {
          bottomSheetModalRef?.current?.dismiss()
          disableBottomSheet()
        }
      }}
      enablePanDownToClose={disableBackdrop ? false : true}
    >
      <Stack
        onLayout={handleContentLayout}
        className="bg-neutral-white100 flex-1"
        style={[{ marginBottom: safeAreaBottom }, bodyStyle]}
      >
        {children}
      </Stack>
    </BottomSheetModal>
  )
}
